/**
 * Determines whether keyboard shortcuts should be blocked based on the current
 * focus state. Returns true if the user appears to be typing in any text input
 * context.
 *
 * This function checks for:
 * - Standard text input elements (input[type=text], textarea)
 * - Contenteditable elements (rich text editors)
 * - Elements with role="textbox" (ARIA accessibility)
 * - Elements inside Bootstrap Vue input components
 * - Elements inside modals with text inputs focused
 */
export function shouldBlockKeyboardShortcuts(): boolean {
    const activeEl = document.activeElement;
    if (!activeEl) return false;

    const tagName = activeEl.tagName.toLowerCase();

    // Block for textarea elements
    if (tagName === 'textarea') return true;

    // Block for standard form elements
    if (tagName === 'select') return true;
    if (tagName === 'button') return true;
    if (tagName === 'a') return true;

    // Block for text input fields (but not checkboxes/radios)
    if (tagName === 'input') {
        const inputType = (activeEl as HTMLInputElement).type?.toLowerCase() || 'text';
        const nonTextTypes = ['checkbox', 'radio', 'button', 'submit', 'reset', 'image'];
        if (!nonTextTypes.includes(inputType)) {
            return true;
        }
    }

    // Block for contenteditable elements (rich text editors)
    if (activeEl.hasAttribute('contenteditable') &&
        activeEl.getAttribute('contenteditable') !== 'false') {
        return true;
    }

    // Block for ARIA textbox role
    if (activeEl.getAttribute('role') === 'textbox') {
        return true;
    }

    return false;
}
